import React, { useState, useEffect } from 'react';
import { getSocket, onSocketChange } from '../socket';
import { DEFAULT_AVATAR } from '../utils/defaultAvatar';
import { DEFAULT_BUBBLE_COLOR, getBubbleHex, isValidBubbleColor, mixHex, rgba } from '../utils/bubbleColors';
import './FriendRequests.css';

// Legacy bucket markers (soft-block): treat these URLs as unavailable in UI.
const LEGACY_STORAGE_MARKERS = (() => {
  try {
    const raw = process.env.REACT_APP_LEGACY_STORAGE_MARKERS || '';
    return raw.split(',').map(s => s.trim()).filter(Boolean);
  } catch {
    return [];
  }
})();
const isLegacyStorageUrl = (url) => {
  if (!LEGACY_STORAGE_MARKERS.length) return false;
  const s = String(url || '');
  return LEGACY_STORAGE_MARKERS.some((m) => s.includes(m));
};
const safeAvatarSrc = (src) => {
  if (!src) return DEFAULT_AVATAR;
  if (isLegacyStorageUrl(src)) return DEFAULT_AVATAR;
  return src;
};

function FriendRequests({ user, onClose }) {
  const [activeTab, setActiveTab] = useState('search'); // 'search', 'received', 'sent'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cleanupCurrentSocket = null;

    const attach = (socket) => {
      if (cleanupCurrentSocket) {
        cleanupCurrentSocket();
        cleanupCurrentSocket = null;
      }
      if (!socket) return;

      // Request friend requests list when socket becomes available
      socket.emit('friend_requests:get');

      // Listen for friend request events
      const onList = (requests) => {
        const received = requests.filter(req => req.to === user.uid);
        const sent = requests.filter(req => req.from === user.uid);
        setReceivedRequests(received);
        setSentRequests(sent);
      };
      const onReceived = (request) => setReceivedRequests(prev => [...prev, request]);
      const onSent = (request) => setSentRequests(prev => [...prev, request]);
      const onAccepted = ({ requestId }) => {
        setReceivedRequests(prev => prev.filter(req => req.id !== requestId));
        setSentRequests(prev => prev.filter(req => req.id !== requestId));
      };
      const onRejected = ({ requestId }) => {
        setReceivedRequests(prev => prev.filter(req => req.id !== requestId));
        setSentRequests(prev => prev.filter(req => req.id !== requestId));
      };
      const onCancelled = ({ requestId }) => {
        setReceivedRequests(prev => prev.filter(req => req.id !== requestId));
        setSentRequests(prev => prev.filter(req => req.id !== requestId));
      };
      const onSearchResults = (results) => {
        setSearchResults(results);
        setIsSearching(false);
      };
      const onError = (error) => {
        setErrorMessage(error.message);
        setTimeout(() => setErrorMessage(''), 5000);
      };

      socket.on('friend_requests:list', onList);
      socket.on('friend_request:received', onReceived);
      socket.on('friend_request:sent', onSent);
      socket.on('friend_request:accepted', onAccepted);
      socket.on('friend_request:rejected', onRejected);
      socket.on('friend_request:cancelled', onCancelled);
      socket.on('user:search:results', onSearchResults);
      socket.on('error', onError);

      cleanupCurrentSocket = () => {
        socket.off('friend_requests:list', onList);
        socket.off('friend_request:received', onReceived);
        socket.off('friend_request:sent', onSent);
        socket.off('friend_request:accepted', onAccepted);
        socket.off('friend_request:rejected', onRejected);
        socket.off('friend_request:cancelled', onCancelled);
        socket.off('user:search:results', onSearchResults);
        socket.off('error', onError);
      };
    };

    const unsubscribe = onSocketChange(attach);
    // Attach immediately if socket already exists
    attach(getSocket());

    return () => {
      if (cleanupCurrentSocket) cleanupCurrentSocket();
      unsubscribe();
    };
  }, [user.uid]);

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    const socket = getSocket();
    socket.emit('user:search', { query: searchQuery });
  };

  const handleSendRequest = (userId) => {
    const socket = getSocket();
    socket.emit('friend_request:send', { recipientId: userId });
    
    // Remove from search results
    setSearchResults(prev => prev.filter(u => u.userId !== userId));
    
    // Clear any previous error
    setErrorMessage('');
  };

  const handleAcceptRequest = (requestId) => {
    const socket = getSocket();
    socket.emit('friend_request:accept', { requestId });
  };

  const handleRejectRequest = (requestId) => {
    const socket = getSocket();
    socket.emit('friend_request:reject', { requestId });
  };

  const handleCancelRequest = (requestId) => {
    const socket = getSocket();
    socket.emit('friend_request:cancel', { requestId });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="friend-requests-overlay" onClick={onClose}>
      <div className="friend-requests-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add People</h2>
          <button className="close-btn" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {errorMessage && (
          <div className="error-message" style={{ 
            padding: '10px', 
            backgroundColor: '#ff4444', 
            color: 'white', 
            borderRadius: '5px', 
            margin: '10px 20px',
            textAlign: 'center'
          }}>
            {errorMessage}
          </div>
        )}

        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => setActiveTab('search')}
          >
            Search Users
          </button>
          <button 
            className={`tab ${activeTab === 'received' ? 'active' : ''}`}
            onClick={() => setActiveTab('received')}
          >
            Received {receivedRequests.length > 0 && `(${receivedRequests.length})`}
          </button>
          <button 
            className={`tab ${activeTab === 'sent' ? 'active' : ''}`}
            onClick={() => setActiveTab('sent')}
          >
            Sent {sentRequests.length > 0 && `(${sentRequests.length})`}
          </button>
        </div>

        <div className="modal-content">
          {activeTab === 'search' && (
            <div className="search-tab">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button onClick={handleSearch} disabled={isSearching}>
                  <span className="material-symbols-outlined">search</span>
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
              </div>

            <div className="results-list">
                {searchResults.length === 0 && !isSearching && searchQuery && (
                  <p className="empty-state">No users found</p>
                )}
                {searchResults.map((user) => (
                  (() => {
                    const key = isValidBubbleColor(user?.bubbleColor) ? user.bubbleColor : DEFAULT_BUBBLE_COLOR;
                    const ring = mixHex(getBubbleHex(key), '#1A1D21', 0.5);
                    return (
                  <div key={user.userId} className="user-result">
                    <img 
                      src={safeAvatarSrc(user.photoURL)}
                      alt={user.username}
                      className="user-avatar"
                      style={{ borderColor: rgba(ring, 0.6) }}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = DEFAULT_AVATAR;
                      }}
                    />
                    <div className="user-info">
                      <span className="username">{user.username}</span>
                      <span className="email">{user.email}</span>
                    </div>
                    <button 
                      className="add-btn"
                      onClick={() => handleSendRequest(user.userId)}
                    >
                      <span className="material-symbols-outlined">person_add</span>
                      Add
                    </button>
                  </div>
                    );
                  })()
                ))}
              </div>
            </div>
          )}

          {activeTab === 'received' && (
            <div className="requests-tab">
              {receivedRequests.length === 0 ? (
                <p className="empty-state">No pending requests</p>
              ) : (
                receivedRequests.map((request) => (
                  <div key={request.id} className="request-item">
                    <img 
                      src={safeAvatarSrc(request.fromPhotoURL)}
                      alt={request.fromUsername}
                      className="user-avatar"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = DEFAULT_AVATAR;
                      }}
                    />
                    <div className="request-info">
                      <span className="username">{request.fromUsername}</span>
                      <span className="email">{request.fromEmail}</span>
                    </div>
                    <div className="request-actions">
                      <button 
                        className="accept-btn"
                        onClick={() => handleAcceptRequest(request.id)}
                      >
                        <span className="material-symbols-outlined">check</span>
                        Accept
                      </button>
                      <button 
                        className="reject-btn"
                        onClick={() => handleRejectRequest(request.id)}
                      >
                        <span className="material-symbols-outlined">close</span>
                        Reject
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'sent' && (
            <div className="requests-tab">
              {sentRequests.length === 0 ? (
                <p className="empty-state">No pending requests</p>
              ) : (
                sentRequests.map((request) => (
                  <div key={request.id} className="request-item">
                    <img 
                      src={safeAvatarSrc(request.toPhotoURL)}
                      alt={request.toUsername}
                      className="user-avatar"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = DEFAULT_AVATAR;
                      }}
                    />
                    <div className="request-info">
                      <span className="username">{request.toUsername}</span>
                      <span className="email">{request.toEmail}</span>
                    </div>
                    <button 
                      className="cancel-btn"
                      onClick={() => handleCancelRequest(request.id)}
                    >
                      <span className="material-symbols-outlined">cancel</span>
                      Cancel
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FriendRequests;

